import { LocalizationProvider } from '@mui/x-date-pickers';
import { LicenseInfo as MuiXLicense } from '@mui/x-license';
import { isNotFalsy } from '@seedcompany/common';
import { ApolloProvider, GqlSensitiveOperations } from './api';
import { LuxonCalenderDateUtils } from './common/LuxonCalenderDateUtils';
import { CommentsProvider } from './components/Comments/CommentsContext';
import { ConfettiProvider } from './components/Confetti';
import { MaintenanceProvider } from './components/MaintenanceBanner';
import { Nest } from './components/Nest';
import { SessionProvider } from './components/Session';
import { SnackbarProvider } from './components/Snackbar';
import { UploadProvider as FileUploadProvider } from './components/Upload';
import { Root } from './scenes/Root';
import { ThemeProvider } from './theme/ThemeProvider';

const logRocketAppId = process.env.RAZZLE_LOG_ROCKET_APP_ID;
if (logRocketAppId) {
  void Promise.all([import('logrocket'), import('logrocket-react')]).then(
    ([LogRocket, setupLogRocketReact]) => {
      LogRocket.default.init(logRocketAppId, {
        shouldParseXHRBlob: true, // Parse API response bodies
        network: {
          requestSanitizer(request) {
            // Relies on operation name suffix which is configured in Apollo HttpLink config
            if (
              [...GqlSensitiveOperations].some((op) =>
                request.url.endsWith(`/${op}`)
              )
            ) {
              request.body = undefined;
            }
            return request;
          },
        },
      });
      if (typeof window !== 'undefined') {
        setupLogRocketReact.default(LogRocket);
      }
    }
  );
}

MuiXLicense.setLicenseKey(process.env.MUI_X_LICENSE_KEY!);

/**
 * Register all app providers here in a flat list.
 * These are used client-side, server-side, and in storybook.
 * This prevents git diff churning
 * Order still matters (the first is the outer most component)
 */
export const appProviders = [
  <ThemeProvider key="theme" />,
  <LocalizationProvider key="i10n" dateAdapter={LuxonCalenderDateUtils} />,
  <SnackbarProvider key="snackbar" />, // needed by apollo
  <ApolloProvider key="apollo" />,
  <SessionProvider key="session" />,
  <FileUploadProvider key="files" />,
  <ConfettiProvider key="confetti" />,
  <CommentsProvider key="comments" />,
  <MaintenanceProvider key="maintenance" />,
].filter(isNotFalsy);

export const App = () => (
  <Nest elements={appProviders}>
    <Root />
  </Nest>
);
